// client/src/components/teacher-portal/VanderbiltAssessmentForm.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  Button,
  CircularProgress,
  Alert,
  Divider,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';

// Vanderbilt Teacher Rating Scale response options
const SYMPTOM_OPTIONS = [
  { value: 0, label: 'Never' },
  { value: 1, label: 'Occasionally' },
  { value: 2, label: 'Often' },
  { value: 3, label: 'Very Often' },
];

const PERFORMANCE_OPTIONS = [
  { value: 1, label: '1 - Excellent' },
  { value: 2, label: '2 - Above Average' },
  { value: 3, label: '3 - Average' },
  { value: 4, label: '4 - Somewhat of a Problem' },
  { value: 5, label: '5 - Problematic' },
];

const VanderbiltAssessmentForm = ({ assessment, onSubmit, submitting }) => {
  const [responses, setResponses] = useState({});
  const [currentSection, setCurrentSection] = useState(0);
  const [error, setError] = useState('');

  // Group questions by category/section
  const sections = React.useMemo(() => {
    if (!assessment?.questions) return [];

    const grouped = {};
    assessment.questions.forEach(q => {
      const sectionName = q.instructions || 'Questions';
      if (!grouped[sectionName]) {
        grouped[sectionName] = [];
      }
      grouped[sectionName].push(q);
    });

    return Object.entries(grouped).map(([name, questions]) => ({
      name,
      questions: questions.sort((a, b) => {
        const aNum = parseInt(a.questionId?.split('_q')[1]) || 0;
        const bNum = parseInt(b.questionId?.split('_q')[1]) || 0;
        return aNum - bNum;
      }),
    }));
  }, [assessment]);

  const isPerformanceQuestion = (question) => {
    // Performance questions typically ask about academic/classroom performance
    // They use 1-5 scale instead of 0-3
    const performanceKeywords = ['performance', 'reading', 'mathematics', 'written', 'classroom', 'academic'];
    const questionText = (question.text || '').toLowerCase();
    return performanceKeywords.some(kw => questionText.includes(kw));
  };

  const getOptionsForQuestion = (question) => {
    // Check if question already has answer options defined
    if (question.answerOptions && question.answerOptions.length > 0) {
      return question.answerOptions.map(opt => ({
        value: opt.value,
        label: opt.label,
      }));
    }

    // Otherwise, determine based on question type
    return isPerformanceQuestion(question) ? PERFORMANCE_OPTIONS : SYMPTOM_OPTIONS;
  };

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: parseInt(value),
    }));
    setError('');
  };

  const validateCurrentSection = () => {
    const currentQuestions = sections[currentSection]?.questions || [];
    const unanswered = currentQuestions.filter(q =>
      q.required !== false && responses[q.questionId] === undefined
    );

    if (unanswered.length > 0) {
      setError(`Please answer all questions before continuing. ${unanswered.length} question(s) remaining.`);
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateCurrentSection()) {
      setCurrentSection(prev => Math.min(prev + 1, sections.length - 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    setCurrentSection(prev => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = () => {
    // Validate all sections
    const allQuestions = sections.flatMap(s => s.questions);
    const unanswered = allQuestions.filter(q =>
      q.required !== false && responses[q.questionId] === undefined
    );

    if (unanswered.length > 0) {
      setError(`Please answer all required questions. ${unanswered.length} question(s) remaining.`);
      return;
    }

    onSubmit(responses);
  };

  const getTotalProgress = () => {
    const allQuestions = sections.flatMap(s => s.questions);
    const answered = allQuestions.filter(q => responses[q.questionId] !== undefined).length;
    return Math.round((answered / allQuestions.length) * 100);
  };

  if (!assessment || sections.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const currentSectionData = sections[currentSection];
  const isLastSection = currentSection === sections.length - 1;

  return (
    <Box>
      {/* Progress */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="body2" color="text.secondary">
            Overall Progress
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getTotalProgress()}%
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={getTotalProgress()} sx={{ height: 8, borderRadius: 1 }} />
      </Box>

      {/* Section Navigation */}
      {sections.length > 1 && (
        <Stepper activeStep={currentSection} alternativeLabel sx={{ mb: 3 }}>
          {sections.map((section, index) => (
            <Step key={section.name} completed={index < currentSection}>
              <StepLabel>{section.name}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Current Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {currentSectionData.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please rate each item based on your observations of the student over the past 6 months.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {currentSectionData.questions.map((question, index) => {
          const options = getOptionsForQuestion(question);
          const questionNum = question.questionId?.split('_q')[1] || index + 1;

          return (
            <Box key={question.questionId} sx={{ mb: 4 }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel
                  component="legend"
                  sx={{
                    color: 'text.primary',
                    '&.Mui-focused': { color: 'text.primary' },
                    mb: 1,
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 'medium' }}>
                    {questionNum}. {question.text}
                  </Box>
                  {question.required !== false && (
                    <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
                  )}
                </FormLabel>
                <RadioGroup
                  value={responses[question.questionId] ?? ''}
                  onChange={(e) => handleResponseChange(question.questionId, e.target.value)}
                  row={options.length <= 4}
                >
                  {options.map(option => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                      sx={{ mr: 3 }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
          );
        })}
      </Paper>

      {/* Navigation Buttons */}
      <Box display="flex" justifyContent="space-between" mt={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          disabled={currentSection === 0}
        >
          Back
        </Button>

        {isLastSection ? (
          <Button
            variant="contained"
            color="primary"
            startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Assessment'}
          </Button>
        ) : (
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={handleNext}
          >
            Next
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default VanderbiltAssessmentForm;
